/**
 * 全局产品导航 — IA / 文案 / 图标对齐 spikex-ai-web/lib/nav.ts
 * 链接暂用占位或现有路由，后续可逐一对接。
 */

import {
  IconNavArrowLeftRight,
  IconNavBot,
  IconNavCompass,
  IconNavHistory,
  IconNavLayoutDashboard,
  IconNavLineChart,
  IconNavTrophy,
  IconNavUserCircle,
  IconNavShieldCheck,
  IconNavBadgeCheck,
  IconNavSlidersHorizontal,
  IconNavPercent,
  IconNavUserPlus,
  IconNavLogOut,
} from "../globalHeader/navIcons";
import { USER_TAG_GRAY_FEATURES } from "@/utils/userTagGrayRelease";

const PLACEHOLDER = "#";

function tr(t, key, fallback) {
  if (!t || !key) return fallback;
  const value = t(key);
  return value && value !== key ? value : fallback;
}

function mapNavItem(item, t) {
  return {
    ...item,
    label: tr(t, item.labelKey, item.labelFallback),
    desc: item.descKey ? tr(t, item.descKey, item.descFallback) : item.desc,
    menu: item.menu?.map((sub) => ({
      ...sub,
      label: tr(t, sub.labelKey, sub.labelFallback),
      desc: sub.descKey ? tr(t, sub.descKey, sub.descFallback) : undefined,
    })),
  };
}

export function buildProductNav({ getUrl, cfdSymbol = "btc_usdt_cfd", t }) {
  const futuresPath = `/futures/contract/${cfdSymbol}`;

  const items = [
    {
      id: "markets",
      labelKey: "header.markets",
      labelFallback: "Markets",
      href: getUrl("/market"),
      match: /^\/market($|[/?#])/,
    },
    {
      id: "trade",
      labelKey: "header.trade",
      labelFallback: "Trade",
      href: getUrl(PLACEHOLDER),
      match: /^\/(trade|tradePro|margin|marginPro|cfd|futures|convert)($|[/?#])/,
      menu: [
        {
          labelKey: "header.ContractAccount",
          labelFallback: "Futures",
          descKey: "nav.futuresDesc",
          descFallback: "Trade perpetuals with leverage",
          href: getUrl(futuresPath),
          icon: IconNavLineChart,
        },
        {
          labelKey: "footer.convertNav",
          labelFallback: "Convert",
          descKey: "nav.convertDesc",
          descFallback: "Zero-fee instant swap",
          href: getUrl("/futures/contract/spot"),
          icon: IconNavArrowLeftRight,
        },
      ],
    },
    {
      id: "ai-lab",
      labelKey: "header.aiLab",
      labelFallback: "AI Lab",
      // PC 默认 Arena（对齐 spikex-ai-web /ai → /ai/arena）；移动端落地会落到 workspace
      href: getUrl("/ai-lab?tab=arena"),
      featured: true,
      pulse: true,
      match: /^\/(ai-lab|aicopy|aiselect|proptrading|ai)($|[/?#])/,
      menu: [
        {
          labelKey: "nav.aiLab.agents",
          labelFallback: "Agents",
          descKey: "nav.aiLab.agentsDesc",
          descFallback: "Discover & deploy trading agents",
          href: getUrl("/aicopy"),
          icon: IconNavBot,
        },
        {
          labelKey: "aiLab.tabLeaderboard",
          labelFallback: "Leaderboard",
          descKey: "nav.aiLab.leaderboardDesc",
          descFallback: "Top agents by return & Sharpe",
          href: getUrl("/ai-lab?tab=leaderboard"),
          icon: IconNavTrophy,
        },
        {
          labelKey: "nav.aiLab.workspace",
          labelFallback: "Workspace",
          descKey: "nav.aiLab.workspaceDesc",
          descFallback: "Your follows, positions & PnL",
          href: getUrl("/ai-lab?tab=workspace"),
          icon: IconNavLayoutDashboard,
        },
        {
          labelKey: "nav.aiLab.arena",
          labelFallback: "Arena",
          descKey: "nav.aiLab.arenaDesc",
          descFallback: "Watch AI models compete, live",
          href: getUrl("/ai-lab?tab=arena"),
          icon: IconNavCompass,
        },
      ],
    },
    {
      id: "rewards",
      labelKey: "nav.rewards",
      labelFallback: "Rewards",
      href: getUrl("/referral"),
      pulse: true,
      match: /^\/(referral|affiliate|rewards|activity\/(coupon|reward-hub|voucher))($|[/?#])/,
    },
    {
      id: "beginner-guide",
      labelKey: "nav.beginnerGuide",
      labelFallback: "Beginner's Guide",
      href: getUrl("/beginner-guide"),
      match: /^\/beginner-guide($|[/?#])/,
      userTagFeature: USER_TAG_GRAY_FEATURES.BEGINNER_GUIDE,
    },
  ];

  return items.map((item) => mapNavItem(item, t));
}

export function buildAssetsMenu({ getUrl, t }) {
  const items = [
    {
      labelKey: "header.overview",
      labelFallback: "Overview",
      descKey: "nav.assetsOverviewDesc",
      descFallback: "Total balance, PnL & AI returns",
      href: getUrl("/wallet/account/overview"),
      icon: IconNavLayoutDashboard,
    },
    {
      labelKey: "header.trading",
      labelFallback: "Trading",
      descKey: "nav.assetsTradingDesc",
      descFallback: "Spot, Futures & AI positions",
      href: getUrl("/wallet/account/trading"),
      icon: IconNavLineChart,
    },
    {
      labelKey: "nav.history",
      labelFallback: "History",
      descKey: "nav.assetsHistoryDesc",
      descFallback: "Transactions & order records",
      href: getUrl("/bills/spot/deposit"),
      icon: IconNavHistory,
    },
  ];

  return items.map((item) => mapNavItem(item, t));
}

export function buildProfileMenu({ t, getUrl }) {
  const items = [
    {
      labelKey: "nav.profileMenu.profile",
      labelFallback: "Profile",
      href: getUrl("/accounts/user/overview"),
      icon: IconNavUserCircle,
    },
    {
      labelKey: "nav.profileMenu.security",
      labelFallback: "Security",
      href: getUrl("/accounts/user/security-center"),
      icon: IconNavShieldCheck,
    },
    {
      labelKey: "nav.profileMenu.verification",
      labelFallback: "Verification",
      href: getUrl("/accounts/user/verification"),
      icon: IconNavBadgeCheck,
    },
    {
      labelKey: "nav.profileMenu.preferences",
      labelFallback: "Preferences",
      href: getUrl("/accounts/user/setting"),
      icon: IconNavSlidersHorizontal,
    },
    {
      labelKey: "nav.profileMenu.feeRate",
      labelFallback: "Fee Rate",
      href: getUrl("/rate"),
      icon: IconNavPercent,
    },
    {
      labelKey: "nav.profileMenu.referral",
      labelFallback: "Referral",
      href: getUrl("/referral"),
      icon: IconNavUserPlus,
    },
  ];

  return items.map((item) => ({
    ...item,
    label: tr(t, item.labelKey, item.labelFallback),
  }));
}

export const PROFILE_LOGOUT_ICON = IconNavLogOut;

export const DEPOSIT_HREF = "/wallet/account/common/deposit";
